/* eslint-disable no-empty */
/* eslint-disable default-case */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ThemeProvider as SCThemeProvider } from 'styled-components';

import { ThemeTypes } from '../types/ThemeTypes';
import { staticColors } from '../styles/theme';
import { IFCWithChildren } from '../interfaces/IFCWithChildren';
import { IDynamicThemeColors } from '../interfaces/IDynamicThemeColors';

interface ICurrentThemeContextData {
  type: ThemeTypes;
  switchThemeType: () => void;
}

const CurrentThemeContext = createContext({} as ICurrentThemeContextData);

const CurrentThemeProvider: IFCWithChildren = ({ children }) => {
  const [type, setType] = useState<ThemeTypes>('dark');

  const switchThemeType = useCallback(() => {
    setType((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const dynamicThemeColors = useMemo((): IDynamicThemeColors => {
    switch (type) {
      case 'dark': {
        return {
          neutral: {
            '1000': '#000000',
            '950': '#0E121B',
            '900': '#181B25',
            '800': '#222530',
            '700': '#2B303B',
            '600': '#525866',
            '500': '#717784',
            '400': '#99A0AE',
            '300': '#CACFD8',
            '200': '#E1E4EA',
            '100': '#F2F5F8',
            '50': '#F5F7FA',
            '0': '#FFFFFF',
          },
        };
      }

      case 'light': {
        return {
          neutral: {
            '1000': '#FFFFFF',
            '950': '#FFFFFF',
            '900': '#F5F7FA',
            '800': '#F2F5F8',
            '700': '#E1E4EA',
            '600': '#CACFD8',
            '500': '#99A0AE',
            '400': '#717784',
            '300': '#525866',
            '200': '#2B303B',
            '100': '#222530',
            '50': '#181B25',
            '0': '#0E121B',
          },
        };
      }
    }
  }, [type]);

  return (
    <CurrentThemeContext.Provider
      value={{
        type,
        switchThemeType,
      }}
    >
      <SCThemeProvider
        theme={{
          ...dynamicThemeColors,
          ...staticColors,
        }}
      >
        {children}
      </SCThemeProvider>
    </CurrentThemeContext.Provider>
  );
};

const useCurrentTheme = (): ICurrentThemeContextData => {
  return useContext(CurrentThemeContext);
};

export { CurrentThemeProvider, useCurrentTheme };
